import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/sonner';
import { AdminLayout, CRMLayout } from '@/components/layout';
import { AuthGuard } from '@/components/shared/AuthGuard';
import { CRMAuthGuard } from '@/components/shared/CRMAuthGuard';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { useAuthStore } from '@/store/authStore';

// Auth Pages
import { LoginPage } from '@/pages/auth/LoginPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';

// Client PWA Prototype (mock data)
import ClientPwaLayout from '@/pages/client-pwa/Layout';
import ClientPwaHomePage from '@/pages/client-pwa/HomePage';
import ClientPwaFilesPage from '@/pages/client-pwa/FilesPage';
import ClientPwaInsightsPage from '@/pages/client-pwa/InsightsPage';
import ClientPwaHelpPage from '@/pages/client-pwa/HelpPage';
import ClientPwaSettingsPage from '@/pages/client-pwa/SettingsPage';

// Client PWA v2 - Studio Edition (premium dark, bento, glassmorphism)
import ClientPwaV2Layout from '@/pages/client-pwa-v2/Layout';
import ClientPwaV2HomePage from '@/pages/client-pwa-v2/HomePage';
import ClientPwaV2FilesPage from '@/pages/client-pwa-v2/FilesPage';
import ClientPwaV2InsightsPage from '@/pages/client-pwa-v2/InsightsPage';
import ClientPwaV2HelpPage from '@/pages/client-pwa-v2/HelpPage';
import ClientPwaV2SettingsPage from '@/pages/client-pwa-v2/SettingsPage';

// Client PWA v3 - The Ledger (editorial light, serif, hairlines)
import ClientPwaV3Layout from '@/pages/client-pwa-v3/Layout';
import ClientPwaV3HomePage from '@/pages/client-pwa-v3/HomePage';
import ClientPwaV3FilesPage from '@/pages/client-pwa-v3/FilesPage';
import ClientPwaV3InsightsPage from '@/pages/client-pwa-v3/InsightsPage';
import ClientPwaV3HelpPage from '@/pages/client-pwa-v3/HelpPage';
import ClientPwaV3MessagesPage from '@/pages/client-pwa-v3/MessagesPage';
import ClientPwaV3SettingsPage from '@/pages/client-pwa-v3/SettingsPage';

// Client PWA v4 - "Dukan" for shopkeepers, bilingual EN/FA/Dari/Pashto
import ClientPwaV4Layout from '@/pages/client/Layout';
import ClientPwaV4HomePage from '@/pages/client/HomePage';
import ClientPwaV4AskPage from '@/pages/client/AskPage';
import ClientPwaV4MoneyPage from '@/pages/client/MoneyPage';
import ClientPwaV4FilesPage from '@/pages/client/FilesPage';
import ClientPwaV4FileChatPage from '@/pages/client/FileChatPage';
import ClientPwaV4TipsPage from '@/pages/client/TipsPage';
import ClientPwaV4SettingsPage from '@/pages/client/SettingsPage';
import ClientLoginPage from '@/pages/client/LoginPage';
import ClientAuthGuard from '@/components/shared/ClientAuthGuard';
import ClientMessagesPage from '@/pages/client-messages/ClientMessagesPage';
import TeamMessagesPage from '@/pages/team-messages/TeamMessagesPage';

// CRM Portal Pages
import {
  CRMLoginPage,
  CRMDashboardPage,
  CRMChatPage,
  CRMHelpPage,
  CRMForgotPasswordPage,
  CRMResetPasswordPage,
  CRMSubscriptionPage,
  CRMMessagesPage,
  CRMDeadlinesPage,
  CRMFinancialsPage,
  CRMAIChatPage,
  CRMCompliancePage,
  CRMToolsPage,
} from '@/pages/crm';

// Intake Admin Pages
import {
  IntakeDashboardPage,
  IntakeClientsPage,
  IntakeClientDetailPage,
  IntakeClientDocumentsPage,
  IntakeDocumentTypesPage,
  SystemDocumentTypesPage,
} from '@/pages/intake';

// Research Pages
import {
  ResearchDashboardPage,
  ResearchResourcesPage,
  ResearchResearchersPage,
  ResearchInstitutionsPage,
  ResearchWorkspacesPage,
  ResearchSearchHistoryPage,
  ResearchImageSearchPage,
  ResearchCitationsPage,
  ResearchQualityAssurancePage,
  ResearchSettingsPage,
  ResearchNetworkPage,
  ResearchPIDsPage,
  ResearchAnalyticsPage,
  ResearchUploadsPage,
  ResearchVocabulariesPage,
} from '@/pages/research';

// Legal Pages
import { TermsPage, PrivacyPolicyPage } from '@/pages/legal';
import HelpPage from '@/pages/legal/HelpPage';

// Settings
import { SettingsPage } from '@/pages/settings';

// Dashboard
import { DashboardPage } from '@/pages/dashboard/DashboardPage';

// Knowledge Base
import { KnowledgeBasePage } from '@/pages/knowledge-base/KnowledgeBasePage';
import { KBChatPage } from '@/pages/knowledge-base/KBChatPage';
import { UrlSourcesPage } from '@/pages/knowledge-base/UrlSourcesPage';
import { GlobalKnowledgeBasePage } from '@/pages/knowledge-base/GlobalKnowledgeBasePage';
import AsicKnowledgeBasePage from '@/pages/knowledge-base/AsicKnowledgeBasePage';
import FairWorkKnowledgeBasePage from '@/pages/knowledge-base/FairWorkKnowledgeBasePage';

// Organization
import { OrganizationPage } from '@/pages/organization/OrganizationPage';
import { DepartmentDetailPage } from '@/pages/organization/DepartmentDetailPage';

// Users
import { UsersPage } from '@/pages/users/UsersPage';

// Tenants (Super Admin)
import { TenantsPage } from '@/pages/tenants';

// Global Knowledge (Super Admin)
import { GlobalKnowledgePage, ATOUploadPage } from '@/pages/global-knowledge';

// AI Settings (Super Admin)
import { AISettingsPage } from '@/pages/ai-settings/AISettingsPage';

// Knowledge Gaps (Super Admin)
import { KnowledgeGapsPage } from '@/pages/knowledge-gaps/KnowledgeGapsPage';

// Integrations
import { XeroIntegrationPage } from '@/pages/integrations';

// Client File Management
import { ClientsPage, ClientFileBrowserPage } from '@/pages/files';
import ImportWizardPage from '@/pages/import/ImportWizardPage';
import { PlanGate } from '@/components/shared/PlanGate';
import FirmDashboardPage from '@/pages/firm/FirmDashboardPage';
import ComplianceCheckerPage from '@/pages/compliance/ComplianceCheckerPage';
import MissingDocumentsPage from '@/pages/compliance/MissingDocumentsPage';
import SuperCompliancePage from '@/pages/compliance/SuperCompliancePage';
import FBTDetectorPage from '@/pages/compliance/FBTDetectorPage';
import CashFlowForecastPage from '@/pages/compliance/CashFlowForecastPage';
import TrustDistributionPage from '@/pages/compliance/TrustDistributionPage';
import CGTCalculatorPage from '@/pages/compliance/CGTCalculatorPage';
import PayrollReviewPage from '@/pages/payroll/PayrollReviewPage';
import PayrollClientDetailPage from '@/pages/payroll/PayrollClientDetailPage';
import WorkflowTemplatesPage from '@/pages/compliance/WorkflowTemplatesPage';
import SmartRemindersPage from '@/pages/compliance/SmartRemindersPage';
import AuditLogPage from '@/pages/settings/AuditLogPage';
import MarketplacePage from '@/pages/marketplace/MarketplacePage';
import FeatureDetailPage from '@/pages/marketplace/FeatureDetailPage';
import HelpCenterPage from '@/pages/help/HelpCenterPage';
import EarningsPage from '@/pages/firm/EarningsPage';
import ManagerLayout from '@/pages/manager/ManagerLayout';
import ManagerLogin from '@/pages/manager/ManagerLogin';
import ManagerPinGate from '@/pages/manager/ManagerPinGate';
import { MA_HOST } from '@/pages/manager/base';
import ManagerHomePage from '@/pages/manager/ManagerHomePage';
import VoicePage from '@/pages/manager/VoicePage';
import CalendarPage from '@/pages/manager/CalendarPage';
import PulsePage from '@/pages/manager/PulsePage';
import DigestPage from '@/pages/manager/DigestPage';
import EmailPage from '@/pages/manager/EmailPage';
import TodoPage from '@/pages/manager/TodoPage';
import ClientCardPage from '@/pages/manager/ClientCardPage';
import PADeadlinesPage from '@/pages/manager/DeadlinesPage';
import TeamPage from '@/pages/manager/TeamPage';
import ManagerTimelinePage from '@/pages/manager/ManagerTimelinePage';
import ManagerStaffPage from '@/pages/manager/ManagerStaffPage';
import MessagesPage from '@/pages/manager/MessagesPage';
import AlertsPage from '@/pages/manager/AlertsPage';
import AuditRiskPage from '@/pages/compliance/AuditRiskPage';
import AuditRiskDetailPage from '@/pages/compliance/AuditRiskDetailPage';
import ScenarioModellingPage from '@/pages/compliance/ScenarioModellingPage';
import OpportunityScannerPage from '@/pages/compliance/OpportunityScannerPage';
import MultiClientQueryPage from '@/pages/compliance/MultiClientQueryPage';
import BASValidatorPage from '@/pages/compliance/BASValidatorPage';
import Div7ACheckerPage from '@/pages/compliance/Div7ACheckerPage';
import ClientReportsPage from '@/pages/reports/ClientReportsPage';
import TaxPlanningPage from '@/pages/compliance/TaxPlanningPage';
import TaxPlanningDetailPage from '@/pages/compliance/TaxPlanningDetailPage';
import SignupPage from '@/pages/auth/SignupPage';
import WelcomePage from '@/pages/auth/WelcomePage';
import BillingPage from '@/pages/settings/BillingPage';
import UpgradePage from '@/pages/settings/UpgradePage';
import RevenueDashboardPage from '@/pages/admin/RevenueDashboardPage';
import DemoRequestsPage from '@/pages/admin/DemoRequestsPage';
import WorkshopsAdminPage from '@/pages/admin/WorkshopsAdminPage';
import WorkshopRegistrantsPage from '@/pages/admin/WorkshopRegistrantsPage';
import KBMonitorPage from '@/pages/admin/KBMonitorPage';
import LandingPage from '@/pages/landing/LandingPage';
import BookDemoPage from '@/pages/landing/BookDemoPage';
import WorkshopsListPage from '@/pages/landing/WorkshopsListPage';
import WorkshopDetailPage from '@/pages/landing/WorkshopDetailPage';
import SmartComparisonPage from '@/pages/intelligence/SmartComparisonPage';
import FailureWarningPage from '@/pages/intelligence/FailureWarningPage';
import FailureWarningDetailPage from '@/pages/intelligence/FailureWarningDetailPage';
import ReconciliationPage from '@/pages/intelligence/ReconciliationPage';

// SMSF
import SMSFDashboardPage from '@/pages/smsf/SMSFDashboardPage';
import SMSFFundDetailPage from '@/pages/smsf/SMSFFundDetailPage';
import SMSFNewFundWizard from '@/pages/smsf/SMSFNewFundWizard';
import SMSFOverviewDashboard from '@/pages/smsf/SMSFOverviewDashboard';
import SMSFContributionsPage from '@/pages/smsf/SMSFContributionsPage';
import SMSFInvestmentCompliancePage from '@/pages/smsf/SMSFInvestmentCompliancePage';
import SMSFPensionsPage from '@/pages/smsf/SMSFPensionsPage';
import SMSFReportingPage from '@/pages/smsf/SMSFReportingPage';
import SMSFComplianceRiskPage from '@/pages/smsf/SMSFComplianceRiskPage';
import SMSFClientPortalPage from '@/pages/smsf/SMSFClientPortalPage';
import SMSFAutomationPage from '@/pages/smsf/SMSFAutomationPage';
import SMSFAdvisoryPage from '@/pages/smsf/SMSFAdvisoryPage';
import SMSFKnowledgePage from '@/pages/smsf/SMSFKnowledgePage';

// Deadlines
import { DeadlinesPage } from '@/pages/deadlines/DeadlinesPage';


// Insights (AI-powered features)
import { ActionListsPage, RevenueOpportunitiesPage, RiskHeatmapPage, ClientTimelinePage, RegulatoryImpactPage, DocumentConsistencyPage } from '@/pages/insights';

// Automation (Document Automation)
import AutomationPage from '@/pages/automation/AutomationPage';
import GenerateFlowPage from '@/pages/automation/GenerateFlowPage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// Default landing page: every authenticated user lands on the dashboard
// directly after login. (Onboarding wizard removed.)
function DefaultLanding() {
  const user = useAuthStore((s) => s.user);
  if (!user) return null;
  return <DashboardPage />;
}

// Onboarding has been removed - anyone who hits /onboarding (stale link /
// URL bar) is bounced straight to the panel.
function OnboardingGate() {
  return <Navigate to="/panel" replace />;
}

// Placeholder pages - will be implemented later
const PlaceholderPage = ({ title }: { title: string }) => (
  <div className="flex h-[50vh] items-center justify-center">
    <div className="text-center">
      <h2 className="text-2xl font-bold">{title}</h2>
      <p className="text-muted-foreground">Coming soon...</p>
    </div>
  </div>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <Toaster position="top-right" richColors />
          <BrowserRouter>
            {MA_HOST ? (
            /* ma.ronidoc.ai - Manager Assistant mounted at the ROOT (no /pa) */
            <Routes>
              <Route path="/login" element={<ManagerLogin />} />
              <Route path="/" element={<ManagerPinGate><ManagerLayout /></ManagerPinGate>}>
                <Route index element={<ManagerHomePage />} />
                <Route path="home" element={<ManagerHomePage />} />
                <Route path="voice" element={<VoicePage />} />
                <Route path="calendar" element={<CalendarPage />} />
                <Route path="pulse" element={<PulsePage />} />
                <Route path="digest" element={<DigestPage />} />
                <Route path="email" element={<EmailPage />} />
                <Route path="todo" element={<TodoPage />} />
                <Route path="deadlines" element={<PADeadlinesPage />} />
                <Route path="team" element={<TeamPage />} />
                <Route path="timeline" element={<ManagerTimelinePage />} />
                <Route path="staff" element={<ManagerStaffPage />} />
                <Route path="messages" element={<MessagesPage />} />
                <Route path="alerts" element={<AlertsPage />} />
                <Route path="client/:clientId" element={<ClientCardPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            ) : (
            <Routes>
              {/* Public landing page */}
              <Route index element={<LandingPage />} />
              <Route path="/demo" element={<BookDemoPage />} />
              <Route path="/workshops" element={<WorkshopsListPage />} />
              <Route path="/workshops/:slug" element={<WorkshopDetailPage />} />

              {/* Auth routes */}
              <Route
                path="/login-redirect"
                element={
                  <AuthGuard requireAuth={false}>
                    <LoginPage />
                  </AuthGuard>
                }
              />
              <Route
                path="/login"
                element={
                  <AuthGuard requireAuth={false}>
                    <LoginPage />
                  </AuthGuard>
                }
              />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/welcome" element={<WelcomePage />} />
              <Route
                path="/forgot-password"
                element={
                  <AuthGuard requireAuth={false}>
                    <ForgotPasswordPage />
                  </AuthGuard>
                }
              />

              {/* Legal pages (public) */}
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/help" element={<HelpPage />} />

              {/* CRM Portal Routes */}
              {/* /crm = Login page (redirects to dashboard if logged in) */}
              <Route
                path="/crm"
                element={
                  <CRMAuthGuard requireAuth={false}>
                    <CRMLoginPage />
                  </CRMAuthGuard>
                }
              />
              {/* Redirect old /crm/login to /crm */}
              <Route path="/crm/login" element={<Navigate to="/crm" replace />} />
              <Route path="/crm/forgot-password" element={<CRMForgotPasswordPage />} />
              <Route path="/crm/reset-password" element={<CRMResetPasswordPage />} />

              {/* Protected CRM routes with layout */}
              <Route
                element={
                  <CRMAuthGuard requireAuth>
                    <CRMLayout />
                  </CRMAuthGuard>
                }
              >
                <Route path="/crm/dashboard" element={<CRMDashboardPage />} />
                <Route path="/crm/chat" element={<CRMChatPage />} />
                <Route path="/crm/help" element={<CRMHelpPage />} />
                <Route path="/crm/profile" element={<PlaceholderPage title="Profile" />} />
                <Route path="/crm/subscription" element={<CRMSubscriptionPage />} />
                <Route path="/crm/messages" element={<CRMMessagesPage />} />
                <Route path="/crm/deadlines" element={<CRMDeadlinesPage />} />
                <Route path="/crm/financials" element={<CRMFinancialsPage />} />
                <Route path="/crm/ai-chat" element={<CRMAIChatPage />} />
                <Route path="/crm/compliance" element={<CRMCompliancePage />} />
                <Route path="/crm/tools" element={<CRMToolsPage />} />
              </Route>
              {/* Redirect old /crm/documents to /crm/dashboard */}
              <Route path="/crm/documents" element={<Navigate to="/crm/dashboard" replace />} />

              {/* Client PWA prototype - mock data, no auth, for design review */}
              <Route path="/client-pwa" element={<ClientPwaLayout />}>
                <Route index element={<ClientPwaHomePage />} />
                <Route path="files" element={<ClientPwaFilesPage />} />
                <Route path="insights" element={<ClientPwaInsightsPage />} />
                <Route path="help" element={<ClientPwaHelpPage />} />
                <Route path="settings" element={<ClientPwaSettingsPage />} />
              </Route>

              {/* Client PWA v2 - Studio Edition */}
              <Route path="/client-pwa-v2" element={<ClientPwaV2Layout />}>
                <Route index element={<ClientPwaV2HomePage />} />
                <Route path="files" element={<ClientPwaV2FilesPage />} />
                <Route path="insights" element={<ClientPwaV2InsightsPage />} />
                <Route path="help" element={<ClientPwaV2HelpPage />} />
                <Route path="settings" element={<ClientPwaV2SettingsPage />} />
              </Route>

              {/* Client PWA v3 - The Ledger */}
              <Route path="/client-pwa-v3" element={<ClientPwaV3Layout />}>
                <Route index element={<ClientPwaV3HomePage />} />
                <Route path="files" element={<ClientPwaV3FilesPage />} />
                <Route path="insights" element={<ClientPwaV3InsightsPage />} />
                <Route path="help" element={<ClientPwaV3HelpPage />} />
                <Route path="messages" element={<ClientPwaV3MessagesPage />} />
                <Route path="settings" element={<ClientPwaV3SettingsPage />} />
              </Route>

              {/* Client PWA v4 - bilingual professional (EN/FA/Dari/Pashto) */}
              <Route path="/client/login" element={<ClientLoginPage />} />
              <Route
                path="/client"
                element={
                  <ClientAuthGuard>
                    <ClientPwaV4Layout />
                  </ClientAuthGuard>
                }
              >
                <Route index element={<ClientPwaV4HomePage />} />
                <Route path="files" element={<ClientPwaV4FilesPage />} />
                <Route path="files/:fileId/chat" element={<ClientPwaV4FileChatPage />} />
                <Route path="ask" element={<ClientPwaV4AskPage />} />
                <Route path="money" element={<ClientPwaV4MoneyPage />} />
                <Route path="tips" element={<ClientPwaV4TipsPage />} />
                <Route path="settings" element={<ClientPwaV4SettingsPage />} />
                {/* Letters + Receipts redirect into Files (consolidated UX) */}
                <Route path="letters" element={<Navigate to="/client/files" replace />} />
                <Route path="receipts" element={<Navigate to="/client/files" replace />} />
              </Route>

            {/* Protected routes under /panel with layout */}
            <Route
              path="/panel"
              element={
                <AuthGuard requireAuth>
                  <AdminLayout />
                </AuthGuard>
              }
            >
              {/* Default landing: super admin → dashboard, others → kb-chat */}
              <Route index element={<DefaultLanding />} />

              {/* Super Admin Routes */}
              <Route
                path="admin/demo-requests"
                element={<AuthGuard superAdminOnly><DemoRequestsPage /></AuthGuard>}
              />
              <Route
                path="admin/workshops"
                element={<AuthGuard superAdminOnly><WorkshopsAdminPage /></AuthGuard>}
              />
              <Route
                path="admin/workshops/:id/registrants"
                element={<AuthGuard superAdminOnly><WorkshopRegistrantsPage /></AuthGuard>}
              />
              <Route
                path="admin/revenue"
                element={<AuthGuard superAdminOnly><RevenueDashboardPage /></AuthGuard>}
              />
              <Route
                path="kb-monitor"
                element={<AuthGuard superAdminOnly><KBMonitorPage /></AuthGuard>}
              />
              <Route
                path="tenants"
                element={
                  <AuthGuard superAdminOnly>
                    <TenantsPage />
                  </AuthGuard>
                }
              />
              <Route
                path="tenants/:id"
                element={
                  <AuthGuard superAdminOnly>
                    <PlaceholderPage title="Tenant Details" />
                  </AuthGuard>
                }
              />
              <Route
                path="global-knowledge"
                element={
                  <AuthGuard requiredModule="global_knowledge">
                    <GlobalKnowledgePage />
                  </AuthGuard>
                }
              />
              <Route
                path="ato-upload"
                element={
                  <AuthGuard superAdminOnly>
                    <ATOUploadPage />
                  </AuthGuard>
                }
              />
              <Route
                path="ai-settings"
                element={
                  <AuthGuard superAdminOnly>
                    <AISettingsPage />
                  </AuthGuard>
                }
              />
              <Route
                path="global-settings"
                element={
                  <AuthGuard superAdminOnly>
                    <PlaceholderPage title="Global Settings" />
                  </AuthGuard>
                }
              />
              <Route
                path="system-health"
                element={
                  <AuthGuard superAdminOnly>
                    <PlaceholderPage title="System Health" />
                  </AuthGuard>
                }
              />
              <Route
                path="system-document-types"
                element={
                  <AuthGuard superAdminOnly>
                    <SystemDocumentTypesPage />
                  </AuthGuard>
                }
              />
              <Route
                path="knowledge-gaps"
                element={
                  <AuthGuard superAdminOnly>
                    <KnowledgeGapsPage />
                  </AuthGuard>
                }
              />

              {/* Organization Routes - accessible to all authenticated users */}
              <Route path="organization" element={<OrganizationPage />} />
              <Route path="organization/:id" element={<DepartmentDetailPage />} />

              {/* Users Routes - accessible to all authenticated users */}
              <Route path="users" element={<UsersPage />} />
              <Route
                path="users/:id"
                element={
                  <AuthGuard tenantAdminOnly>
                    <PlaceholderPage title="User Details" />
                  </AuthGuard>
                }
              />
              <Route
                path="roles"
                element={
                  <AuthGuard tenantAdminOnly>
                    <PlaceholderPage title="Roles & Permissions" />
                  </AuthGuard>
                }
              />
              <Route
                path="org-settings"
                element={
                  <AuthGuard tenantAdminOnly>
                    <PlaceholderPage title="Organization Settings" />
                  </AuthGuard>
                }
              />
              <Route
                path="billing"
                element={
                  <AuthGuard tenantAdminOnly>
                    <BillingPage />
                  </AuthGuard>
                }
              />
              <Route
                path="upgrade"
                element={
                  <AuthGuard tenantAdminOnly>
                    <UpgradePage />
                  </AuthGuard>
                }
              />
              <Route
                path="audit-logs"
                element={
                  <AuthGuard tenantAdminOnly>
                    <AuditLogPage />
                  </AuthGuard>
                }
              />

              {/* Integrations (Tenant Admin) */}
              <Route
                path="integrations/xero"
                element={
                  <AuthGuard tenantAdminOnly>
                    <XeroIntegrationPage />
                  </AuthGuard>
                }
              />

              {/* Common Routes (all authenticated users) */}
              <Route
                path="reports-old"
                element={<PlaceholderPage title="Reports" />}
              />
              <Route
                path="reports-old/:id"
                element={<PlaceholderPage title="Report Viewer" />}
              />
              {/* Firm Dashboard (Pro+) */}
              <Route path="firm/dashboard" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={2} featureName="Firm Dashboard"><FirmDashboardPage /></PlanGate></AuthGuard>} />

              {/* Compliance Routes */}
              <Route path="compliance/checker" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={2} featureName="ATO Compliance Checker"><ComplianceCheckerPage /></PlanGate></AuthGuard>} />
              <Route path="compliance/audit-risk" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="Audit Risk Predictor"><AuditRiskPage /></PlanGate></AuthGuard>} />
              <Route path="compliance/audit-risk/:clientId" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="Audit Risk Predictor"><AuditRiskDetailPage /></PlanGate></AuthGuard>} />
              <Route path="compliance/scenarios" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="What-If Scenario Modelling"><ScenarioModellingPage /></PlanGate></AuthGuard>} />
              <Route path="multi-client" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="Multi-Client Intelligence"><MultiClientQueryPage /></PlanGate></AuthGuard>} />
              <Route path="reports" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={2} featureName="Client Reports"><ClientReportsPage /></PlanGate></AuthGuard>} />
              <Route path="intelligence/comparison" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={2} featureName="Smart Document Comparison"><SmartComparisonPage /></PlanGate></AuthGuard>} />
              <Route path="intelligence/failure-warning" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="Business Risk Warning"><FailureWarningPage /></PlanGate></AuthGuard>} />
              <Route path="intelligence/failure-warning/:clientId" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="Business Risk Warning"><FailureWarningDetailPage /></PlanGate></AuthGuard>} />
              <Route path="intelligence/reconciliation" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={2} featureName="Auto-Reconciliation"><ReconciliationPage /></PlanGate></AuthGuard>} />
              <Route path="compliance/tax-planning" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="Year-End Tax Planning"><TaxPlanningPage /></PlanGate></AuthGuard>} />
              <Route path="compliance/tax-planning/:clientId" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="Year-End Tax Planning"><TaxPlanningDetailPage /></PlanGate></AuthGuard>} />
              <Route path="compliance/bas" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={2} featureName="BAS Validator"><BASValidatorPage /></PlanGate></AuthGuard>} />
              <Route path="compliance/div7a" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={2} featureName="Division 7A Checker"><Div7ACheckerPage /></PlanGate></AuthGuard>} />
              <Route path="compliance/missing-documents" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={2} featureName="Missing Document Detector"><MissingDocumentsPage /></PlanGate></AuthGuard>} />
              <Route path="compliance/super" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="Superannuation Compliance"><SuperCompliancePage /></PlanGate></AuthGuard>} />
              <Route path="compliance/fbt" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="FBT Detector"><FBTDetectorPage /></PlanGate></AuthGuard>} />
              <Route path="compliance/cashflow" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="Cash Flow Forecasting"><CashFlowForecastPage /></PlanGate></AuthGuard>} />
              <Route path="compliance/trust-distribution" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="Trust Distribution Planner"><TrustDistributionPage /></PlanGate></AuthGuard>} />
              <Route path="compliance/cgt" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="CGT Calculator"><CGTCalculatorPage /></PlanGate></AuthGuard>} />
              <Route path="payroll" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={2} featureName="Payroll Review"><PayrollReviewPage /></PlanGate></AuthGuard>} />
              <Route path="payroll/:clientId" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={2} featureName="Payroll Review"><PayrollClientDetailPage /></PlanGate></AuthGuard>} />
              <Route path="compliance/workflows" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="Workflow Templates"><WorkflowTemplatesPage /></PlanGate></AuthGuard>} />
              <Route path="compliance/reminders" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={2} featureName="Smart Reminders"><SmartRemindersPage /></PlanGate></AuthGuard>} />
              <Route path="compliance/opportunities" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="Tax Opportunity Scanner"><OpportunityScannerPage /></PlanGate></AuthGuard>} />

              {/* SMSF Routes */}
              <Route path="smsf" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="SMSF Management"><SMSFOverviewDashboard /></PlanGate></AuthGuard>} />
              <Route path="smsf/funds" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="SMSF Management"><SMSFDashboardPage /></PlanGate></AuthGuard>} />
              <Route path="smsf/new" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="SMSF Management"><SMSFNewFundWizard /></PlanGate></AuthGuard>} />
              <Route path="smsf/:fundId" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="SMSF Management"><SMSFFundDetailPage /></PlanGate></AuthGuard>} />
              <Route path="smsf/contributions" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="SMSF Contributions"><SMSFContributionsPage /></PlanGate></AuthGuard>} />
              <Route path="smsf/investments" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="SMSF Investment Compliance"><SMSFInvestmentCompliancePage /></PlanGate></AuthGuard>} />
              <Route path="smsf/pensions" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="SMSF Pensions"><SMSFPensionsPage /></PlanGate></AuthGuard>} />
              <Route path="smsf/reporting" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="SMSF Reporting"><SMSFReportingPage /></PlanGate></AuthGuard>} />
              <Route path="smsf/compliance" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="SMSF Compliance"><SMSFComplianceRiskPage /></PlanGate></AuthGuard>} />
              <Route path="smsf/portal" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="SMSF Client Portal"><SMSFClientPortalPage /></PlanGate></AuthGuard>} />
              <Route path="smsf/automation" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="SMSF Automation"><SMSFAutomationPage /></PlanGate></AuthGuard>} />
              <Route path="smsf/advisory" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="SMSF Advisory"><SMSFAdvisoryPage /></PlanGate></AuthGuard>} />
              <Route path="smsf/knowledge" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={3} featureName="SMSF Knowledge"><SMSFKnowledgePage /></PlanGate></AuthGuard>} />

              {/* Automation Routes */}
              <Route path="automation" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={2} featureName="Automation"><AutomationPage /></PlanGate></AuthGuard>} />
              <Route path="automation/generate" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={2} featureName="Automation"><GenerateFlowPage /></PlanGate></AuthGuard>} />
              <Route path="automation/generate/:templateId" element={<AuthGuard requiredModule="accounting_docs"><PlanGate minPlan={2} featureName="Automation"><GenerateFlowPage /></PlanGate></AuthGuard>} />

              {/* Client File Management Routes - requires accounting_docs module */}
              <Route
                path="files/clients"
                element={
                  <AuthGuard requiredModule="accounting_docs">
                    <ClientsPage />
                  </AuthGuard>
                }
              />
              <Route
                path="files/clients/:clientId"
                element={
                  <AuthGuard requiredModule="accounting_docs">
                    <ClientFileBrowserPage />
                  </AuthGuard>
                }
              />
              <Route
                path="files"
                element={<Navigate to="/panel/files/clients" replace />}
              />
              {/* Client Messages - firm replies to mobile-PWA clients */}
              <Route
                path="client-messages"
                element={
                  <AuthGuard>
                    <ClientMessagesPage />
                  </AuthGuard>
                }
              />
              {/* Team Chat - internal staff <-> staff messaging (incl. manager) */}
              <Route
                path="team-messages"
                element={
                  <AuthGuard>
                    <TeamMessagesPage />
                  </AuthGuard>
                }
              />
              {/* Import Documents - bulk import wizard from local / cloud drives */}
              <Route
                path="import"
                element={
                  <AuthGuard requiredModule="accounting_docs">
                    <PlanGate minPlan={2} featureName="Import Documents">
                      <ImportWizardPage />
                    </PlanGate>
                  </AuthGuard>
                }
              />
              {/* Deadlines Route - requires accounting_docs module */}
              <Route
                path="deadlines"
                element={
                  <AuthGuard requiredModule="accounting_docs">
                    <PlanGate minPlan={2} featureName="Deadline Risk Alerts">
                      <DeadlinesPage />
                    </PlanGate>
                  </AuthGuard>
                }
              />
              {/* Insights Routes */}
              <Route
                path="insights/actions"
                element={<ActionListsPage />}
              />
              <Route
                path="insights/opportunities"
                element={<RevenueOpportunitiesPage />}
              />
              <Route
                path="insights/risk-heatmap"
                element={<RiskHeatmapPage />}
              />
              <Route
                path="insights/timeline"
                element={<ClientTimelinePage />}
              />
              <Route
                path="insights/regulatory-impact"
                element={<RegulatoryImpactPage />}
              />
              <Route
                path="insights/document-consistency"
                element={<DocumentConsistencyPage />}
              />
              <Route
                path="knowledge-base"
                element={<KnowledgeBasePage />}
              />
              <Route
                path="ato-knowledge"
                element={
                  <AuthGuard requiredModule="accounting_docs">
                    <GlobalKnowledgeBasePage />
                  </AuthGuard>
                }
              />
              <Route
                path="asic-knowledge"
                element={
                  <AuthGuard requiredModule="accounting_docs">
                    <AsicKnowledgeBasePage />
                  </AuthGuard>
                }
              />
              <Route
                path="fair-work-knowledge"
                element={
                  <AuthGuard requiredModule="accounting_docs">
                    <FairWorkKnowledgeBasePage />
                  </AuthGuard>
                }
              />
              <Route
                path="kb-chat"
                element={<KBChatPage />}
              />
              <Route
                path="url-sources"
                element={<UrlSourcesPage />}
              />
              <Route
                path="api-keys"
                element={
                  <AuthGuard tenantAdminOnly>
                    <PlaceholderPage title="API Keys" />
                  </AuthGuard>
                }
              />

              {/* Intake Automation Routes (Tenant Admin + intake module) */}
              <Route
                path="intake"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="intake">
                    <IntakeDashboardPage />
                  </AuthGuard>
                }
              />
              <Route
                path="intake/clients"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="intake">
                    <IntakeClientsPage />
                  </AuthGuard>
                }
              />
              <Route
                path="intake/clients/:id"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="intake">
                    <IntakeClientDetailPage />
                  </AuthGuard>
                }
              />
              <Route
                path="intake/clients/:clientId/edit"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="intake">
                    <IntakeClientDetailPage editMode />
                  </AuthGuard>
                }
              />
              <Route
                path="intake/clients/:clientId/documents"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="intake">
                    <IntakeClientDocumentsPage />
                  </AuthGuard>
                }
              />
              <Route
                path="intake/document-types"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="intake">
                    <IntakeDocumentTypesPage />
                  </AuthGuard>
                }
              />
              <Route
                path="intake/faqs"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="intake">
                    <PlaceholderPage title="Manage FAQs" />
                  </AuthGuard>
                }
              />

              {/* Research Intelligence Routes (Tenant Admin + research module) */}
              <Route
                path="research"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="research">
                    <ResearchDashboardPage />
                  </AuthGuard>
                }
              />
              <Route
                path="research/resources"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="research">
                    <ResearchResourcesPage />
                  </AuthGuard>
                }
              />
              <Route
                path="research/researchers"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="research">
                    <ResearchResearchersPage />
                  </AuthGuard>
                }
              />
              <Route
                path="research/institutions"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="research">
                    <ResearchInstitutionsPage />
                  </AuthGuard>
                }
              />
              <Route
                path="research/workspaces"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="research">
                    <ResearchWorkspacesPage />
                  </AuthGuard>
                }
              />
              <Route
                path="research/search-history"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="research">
                    <ResearchSearchHistoryPage />
                  </AuthGuard>
                }
              />
              <Route
                path="research/image-search"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="research">
                    <ResearchImageSearchPage />
                  </AuthGuard>
                }
              />
              <Route
                path="research/citations"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="research">
                    <ResearchCitationsPage />
                  </AuthGuard>
                }
              />
              <Route
                path="research/quality-assurance"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="research">
                    <ResearchQualityAssurancePage />
                  </AuthGuard>
                }
              />
              <Route
                path="research/settings"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="research">
                    <ResearchSettingsPage />
                  </AuthGuard>
                }
              />
              <Route
                path="research/network"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="research">
                    <ResearchNetworkPage />
                  </AuthGuard>
                }
              />
              <Route
                path="research/pids"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="research">
                    <ResearchPIDsPage />
                  </AuthGuard>
                }
              />
              <Route
                path="research/analytics"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="research">
                    <ResearchAnalyticsPage />
                  </AuthGuard>
                }
              />
              <Route
                path="research/uploads"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="research">
                    <ResearchUploadsPage />
                  </AuthGuard>
                }
              />
              <Route
                path="research/vocabularies"
                element={
                  <AuthGuard tenantAdminOnly requiredModule="research">
                    <ResearchVocabulariesPage />
                  </AuthGuard>
                }
              />

              {/* User Settings */}
              <Route path="profile" element={<PlaceholderPage title="Profile" />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="features" element={<MarketplacePage />} />
              <Route path="features/:slug" element={<FeatureDetailPage />} />
              <Route path="help" element={<HelpCenterPage />} />
              <Route path="firm/earnings" element={<EarningsPage />} />
            </Route>

            {/* Onboarding Wizard - tenant admins only.
                Regular users redirected to KB chat; super admins to dashboard. */}
            <Route
              path="/onboarding"
              element={
                <AuthGuard requireAuth>
                  <OnboardingGate />
                </AuthGuard>
              }
            />

            {/* Manager Assistant - phone + PIN login (not Cognito-gated) */}
            <Route path="/pa/login" element={<ManagerLogin />} />

            {/* Manager Assistant - Standalone PWA (phone+PIN auth) */}
            <Route
              path="/pa"
              element={
                <ManagerPinGate>
                  <ManagerLayout />
                </ManagerPinGate>
              }
            >
              <Route index element={<ManagerHomePage />} />
              <Route path="home" element={<ManagerHomePage />} />
              <Route path="voice" element={<VoicePage />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="pulse" element={<PulsePage />} />
              <Route path="digest" element={<DigestPage />} />
              <Route path="email" element={<EmailPage />} />
              <Route path="todo" element={<TodoPage />} />
              <Route path="deadlines" element={<PADeadlinesPage />} />
              <Route path="team" element={<TeamPage />} />
              <Route path="timeline" element={<ManagerTimelinePage />} />
              <Route path="staff" element={<ManagerStaffPage />} />
              <Route path="messages" element={<MessagesPage />} />
              <Route path="alerts" element={<AlertsPage />} />
              <Route path="client/:clientId" element={<ClientCardPage />} />
            </Route>

            {/* Catch all - redirect to login */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
            )}
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
